import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"
import Sortable from "sortablejs"

// Connects to data-controller="formula-input"
//
// Replaces jQuery UI Sortable for formula field drag & drop
//
// Allows dragging report columns into formula textarea to insert column references
// Example: Drag "students_count" column → Inserts "%{students_count}%{_ROW_}" at cursor
//
// Usage:
//   = f.input :value,
//       input_html: {
//         class: 'formula-input',
//         data: {
//           controller: "formula-input"
//         }
//       }
//
export default class extends Controller {
  connect() {
    
    // Check if we've already wrapped this textarea
    if (this.element.parentNode?.classList?.contains('formula-input-drop-zone')) {
      this.wrapper = this.element.parentNode
      
      // Check if sortable exists
      if (this.wrapper._sortable) {
        return
      }
    }
    
    // Create wrapper if we don't have one yet
    if (!this.wrapper) {
      // Sortable.js doesn't work well with textarea elements directly
      // We need to wrap the textarea in a div and make that div the drop target
      // But since we're attaching to the textarea via data-controller, we'll create
      // a wrapper div and move the textarea into it
      
      const wrapper = document.createElement('div')
      wrapper.className = 'formula-input-drop-zone'
      wrapper.style.position = 'relative'
      wrapper.style.minHeight = '100px'
      wrapper.style.padding = '4px'
      wrapper.style.border = '2px dashed transparent' // Reserve space but invisible
      
      // Insert wrapper before textarea
      this.element.parentNode.insertBefore(wrapper, this.element)
      
      // Move textarea into wrapper
      wrapper.appendChild(this.element)
      
      // Store reference to wrapper
      this.wrapper = wrapper
    }
    
    // Make wrapper accept drops from report-columns group
    this.sortable = Sortable.create(this.wrapper, {
      group: {
        name: 'formula-drop',
        pull: false,                    // Don't allow dragging out
        put: ['report-columns']         // Accept drops from report-columns group
      },

      sort: false,                      // Don't allow reordering
      animation: 0,                     // No animation
      
      // Hide the ghost element completely
      ghostClass: 'formula-drop-ghost',
      
      // Allow dropping anywhere in the wrapper
      draggable: '.column-item',        // Only accept column items
      
      onAdd: (evt) => {
        
        // Clear the hover styles
        this.wrapper.style.borderColor = 'transparent'
        this.wrapper.style.backgroundColor = ''
        
        this.handleColumnDrop(evt)
      },
      
      onMove: (evt) => {
        
        // Show border when hovering with dragged element
        this.wrapper.style.borderColor = '#0d6efd'
        this.wrapper.style.backgroundColor = 'rgba(13, 110, 253, 0.05)'
        return true // Allow the move
      }
    })
    
    // Add global drag end listener to remove hover styles
    document.addEventListener('dragend', () => {
      if (this.wrapper) {
        this.wrapper.style.borderColor = 'transparent'
        this.wrapper.style.backgroundColor = ''
        this.wrapper.classList.remove('drag-over')
      }
    })
    
    // Also listen to Sortable's onEnd but on the wrapper
    const originalOnEnd = this.sortable.options.onEnd
    this.sortable.option('onEnd', (evt) => {
      this.wrapper.style.borderColor = 'transparent'
      this.wrapper.style.backgroundColor = ''
      this.wrapper.classList.remove('drag-over')
      if (originalOnEnd) {
        originalOnEnd(evt)
      }
    })
    
    // Use native drag events for better hover detection
    this.wrapper.addEventListener('dragenter', (e) => {
      e.preventDefault()
      this.wrapper.style.borderColor = '#0d6efd'
      this.wrapper.style.backgroundColor = 'rgba(13, 110, 253, 0.05)'
    })
    
    this.wrapper.addEventListener('dragover', (e) => {
      e.preventDefault() // Necessary to allow dropping
      // Keep the styles visible
      this.wrapper.style.borderColor = '#0d6efd'
      this.wrapper.style.backgroundColor = 'rgba(13, 110, 253, 0.05)'
    })
    
    this.wrapper.addEventListener('dragleave', (e) => {
      // Only clear if we're actually leaving the wrapper (not entering a child)
      if (e.target === this.wrapper) {
        this.wrapper.style.borderColor = 'transparent'
        this.wrapper.style.backgroundColor = ''
      }
    })
    
    this.wrapper.addEventListener('drop', (e) => {
      this.wrapper.style.borderColor = 'transparent'
      this.wrapper.style.backgroundColor = ''
    })
    
  }

  disconnect() {
    // Sortable.js cleanup handled automatically
  }

  handleColumnDrop(evt) {
    
    const item = evt.item
    const fromList = evt.from
    const oldIndex = evt.oldIndex
    const clone = evt.clone  // If this was cloned, evt.clone contains the original
    
    // Check if item came from the selected columns list (not from available columns)
    // The selected columns list has DOM id="selectedColumnsList" and list-id="reportColumnsList"
    const fromSelectedColumnsList = fromList?.id === 'selectedColumnsList' ||
                                     fromList?.id === 'reportColumnsList' ||
                                     fromList?.closest('.column-list')?.id === 'reportColumnsList'
    
    // REJECT drops from available columns lists
    if (!fromSelectedColumnsList) {
      // Check if this was a cloneable item
      const isCloneable = item.classList.contains('cloneable')

      if (isCloneable) {
        item.remove()
      } else {
        if (oldIndex < fromList.children.length) {
          fromList.insertBefore(item, fromList.children[oldIndex])
        } else {
          fromList.appendChild(item)
        }
      }
      return
    }

    // Extract column_ref from dropped item
    const columnRefInput = item.querySelector('input[name$="[column_ref]"]')
    const columnRef = columnRefInput?.value
    
    if (columnRef) {
      const textToInsert = `%{${columnRef}}%{_ROW_}`
      this.insertAtCursor(textToInsert)
    } else {
      console.warn('[Formula Input] ✗ No column_ref found!')
    }

    // CRITICAL: Revert the drag operation (move item back)
    // Since we changed the pull logic to only clone from available->reportColumnsList,
    // ALL drops to formula textarea are MOVE operations, not clones.
    // So we just move the item back to its original position.

    // Insert back at original position
    if (oldIndex < fromList.children.length) {
      fromList.insertBefore(item, fromList.children[oldIndex])
    } else {
      fromList.appendChild(item)
    }
    
  }

  insertAtCursor(text) {
    const textarea = this.element
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const value = textarea.value

    // Insert text at cursor position
    textarea.value = value.substring(0, start) + text + value.substring(end)

    // Move cursor after inserted text
    textarea.selectionStart = textarea.selectionEnd = start + text.length

    // Focus back on textarea
    textarea.focus()

    // Trigger input event for any listeners (e.g., form validation)
    textarea.dispatchEvent(new Event('input', { bubbles: true }))
  }
}
